import { createElement, ElementTypes, init, Payouts } from '@airwallex/components-sdk';
import { logger } from '@jurnee/common/src/browser/Logger';
import { Loader } from '@jurnee/common/src/components/Loader';
import { AirwallexBeneficiary } from '@jurnee/common/src/services/AirwallexService/types';
import { getBeneficiaryComponentAuth } from 'api/payoutBeneficiaries';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface Props {
  partnerId: number;
  beneficiary?: AirwallexBeneficiary;
  onReady(): void;
  onLoadingChange(isLoading: boolean): void;
  onError(): void;
}

export interface AirwallexBeneficiaryFormRef {
  submit: ElementTypes['beneficiaryForm']['submit'];
}

function getDefaultValues(beneficiaryContact?: AirwallexBeneficiary): Payouts.BeneficiaryFormDefaultValues {
  if (beneficiaryContact) {
    const { nickname, transfer_methods, beneficiary } = beneficiaryContact;

    return {
      beneficiary: beneficiary as Payouts.BeneficiaryFormDefaultValues['beneficiary'],
      nickname,
      transfer_methods: transfer_methods as Payouts.BeneficiaryFormDefaultValues['transfer_methods'],
    };
  }

  return {
    beneficiary: {
      entity_type: 'COMPANY' as const,
    }
  };
}

function BeneficiaryForm(props: Props, ref: React.Ref<AirwallexBeneficiaryFormRef>) {
  const { partnerId, beneficiary, onReady, onLoadingChange, onError } = props;
  const elementRef = useRef<ElementTypes['beneficiaryForm']>(null);
  const isReadyRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (!elementRef.current) {
        throw new Error('Element not initialized');
      }
      return elementRef.current.submit();
    }
  }));

  async function load() {
    try {
      const componentAuth = await getBeneficiaryComponentAuth(partnerId);

      await init({
        env: process.env.APP_ENV === 'production' ? 'prod' : 'demo',
        locale: 'en',
        enabledElements: ['payouts'],
        ...componentAuth
      });

      const element = await createElement('beneficiaryForm', {
        defaultValues: getDefaultValues(beneficiary),
        customizations: {
          ui: {
            hideFeeConfig: true,
            hideFlightTime: true,
            hideOptionalFields: true,
          },
          layout: [
            { name: 'conditions', title: '' },
          ],
        }
      });

      element.mount('#beneficiary-form-container');
      elementRef.current = element;

      element.on('ready', () => {
        isReadyRef.current = true;
        setIsLoading(false);
        onReady();
      });

      element.on('formState', ({ loading }) => {
        if (loading || isReadyRef.current) {
          onLoadingChange(loading);
        }
      });

      element.on('error', (error) => {
        logger.error('Airwallex - Form error:', { error });
        setIsLoading(false);
        onError();
      });
    } catch (error) {
      logger.error('Airwallex - Failed to load form:', { error });
      setIsLoading(false);
      onError();
    }
  };

  useEffect(() => {
    load();

    return () => {
      if (elementRef.current) {
        elementRef.current.unmount();
        elementRef.current.destroy();
        elementRef.current = null;
      }
    };
  }, [partnerId]);

  return (
    <>
      { isLoading && <Loader /> }
      <div id="beneficiary-form-container" />
    </>
  );
}

export const AirwallexBeneficiaryForm = forwardRef<AirwallexBeneficiaryFormRef, Props>(
  BeneficiaryForm
);